'use client';
import { AtomicSearchInterfaceWrapper } from '@components/Search/AtomicSearchInterfaceWrapper';
import { TrackPromotionImpression } from '@components/TrackPromotionImpression/TrackPromotionImpression';
import { usePromoTrackingEvents } from '@components/TrackPromotionImpression/usePromoTrackingEvents';
import { EntitlementsProvider } from '@contexts/EntitlementsContext';
import {
  AtomicListingContext,
  AtomicListingProvider,
} from '@contexts/coveo/AtomicListingContext';
import { HeadlessListingProvider } from '@contexts/coveo/HeadlessListingProvider';
import { SortBy, SortCriterion } from '@coveo/atomic-react';
import { useFlags } from '@lib/flags/amplitude/client';
import { FLAG_TREATMENT, Flags } from '@lib/flags/flagConstants';
import { DynamicProductListing as DynamicProductListingType } from '@lib/types/cms/DynamicProductListing';
import {
  buildQueryExpressionWithCMSInputs,
  convertCMSSortOrderToCoveoCriteria,
} from '@lib/util/coveo/cmsToCoveoFieldConversions';
import { toNonNullable } from '@lib/util/toNonNullable';
import { sub } from 'date-fns';
import { useMemo } from 'react';
import { formatTitleForID } from 'utils';
import { DynamicProductListingCards } from './DynamicProductListingCards';
import { DynamicProductListingHeading } from './DynamicProductListingHeading';

const DEFAULT_NUMBER_OF_PRODUCTS = 4;
const DEFAULT_NUMBER_OF_COLUMNS = 4;

export type DynamicProductListingProps = {
  data: DynamicProductListingType;
  position: number;
};

const DynamicProductListing = ({
  data,
  position,
}: DynamicProductListingProps) => {
  const {
    title,
    titleInternal,
    description,
    text,
    maxNumberOfProducts,
    categoriesFilter,
    priceMaximumFilter,
    priceMinimumFilter,
    publishersFilter,
    ratingFilter,
    saleFilter,
    unityVersionFilter,
    sortOrder,
    searchQuery,
    linkTitle,
    linkHref,
    trackAsPromo,
    publishedSinceUnit,
    publishedSinceValue,
    discountMinimumFilter,
    productColumnCount,
    tagFilter,
  } = data;

  const { flags } = useFlags();
  const isHeadless = flags[Flags.SEARCH_V2]?.value === FLAG_TREATMENT;

  const publishedAtSinceDate =
    publishedSinceUnit && publishedSinceValue
      ? sub(new Date(), {
          [publishedSinceUnit]: publishedSinceValue,
        })
      : undefined;

  const queryExpression = buildQueryExpressionWithCMSInputs({
    categories: toNonNullable(categoriesFilter),
    priceMin: toNonNullable(priceMinimumFilter),
    priceMax: toNonNullable(priceMaximumFilter),
    publishers: toNonNullable(publishersFilter),
    rating: toNonNullable(ratingFilter),
    sales: saleFilter ?? undefined,
    unityVersion: toNonNullable(unityVersionFilter),
    publishedAt: publishedAtSinceDate,
    filterByDiscount: toNonNullable(discountMinimumFilter),
    tag: toNonNullable(tagFilter),
  });
  const sortCriteriaInput = useMemo(
    () => ({
      name: sortOrder,
      criterion: sortOrder
        ? convertCMSSortOrderToCoveoCriteria(sortOrder)
        : ({ by: SortBy.Relevancy } as SortCriterion),
    }),
    [sortOrder]
  );
  const numberOfResults = maxNumberOfProducts ?? DEFAULT_NUMBER_OF_PRODUCTS;
  const uniqueId = `DynamicProductListing-${titleInternal ? formatTitleForID(titleInternal) : ''}`;
  const link =
    linkHref && linkTitle ? { href: linkHref, label: linkTitle } : undefined;

  const { promoClickTrackingEvent, promoViewTrackingEvent } =
    usePromoTrackingEvents({
      title: titleInternal || '',
      typename: 'DynamicProductListing',
      position,
      trackAsPromo: trackAsPromo ?? false,
      ctas: link?.label ? [link.label] : [],
    });

  const listingContent = (
    <>
      <DynamicProductListingHeading
        title={title}
        description={description}
        text={text}
        link={link}
        onLinkClick={promoClickTrackingEvent}
      />
      <EntitlementsProvider>
        <DynamicProductListingCards
          gridCols={productColumnCount ?? DEFAULT_NUMBER_OF_COLUMNS}
        />
      </EntitlementsProvider>
    </>
  );

  return (
    <TrackPromotionImpression
      onImpression={promoViewTrackingEvent}
      shouldTrack={trackAsPromo ?? false}
    >
      {isHeadless ? (
        <HeadlessListingProvider
          uniqueId={uniqueId}
          queryExpression={queryExpression}
          sortCriteria={sortCriteriaInput}
          searchQuery={toNonNullable(searchQuery)}
          numberOfResults={numberOfResults}
        >
          {listingContent}
        </HeadlessListingProvider>
      ) : (
        <AtomicListingProvider
          uniqueId={uniqueId}
          queryExpression={queryExpression}
          sortCriteria={sortCriteriaInput}
          searchQuery={toNonNullable(searchQuery)}
          numberOfResults={numberOfResults}
        >
          <AtomicSearchInterfaceWrapper
            shouldExecuteFirstSearch
            reflectStateInUrl={false}
            context={AtomicListingContext}
            id={uniqueId}
          >
            {listingContent}
          </AtomicSearchInterfaceWrapper>
        </AtomicListingProvider>
      )}
    </TrackPromotionImpression>
  );
};

export default DynamicProductListing;
